/**
 * Badges d'état.
 *
 * Règle d'accessibilité tenue partout : la couleur ne porte JAMAIS l'information seule. Chaque
 * badge affiche un point coloré ET son libellé. Un opérateur daltonien, une capture d'écran en
 * noir et blanc collée dans un ticket, une impression : l'information survit.
 *
 * Les correspondances état → couleur viennent du contrat partagé, pas d'un choix local : le
 * marqueur rouge du back-office et le marqueur rouge de l'application mobile désignent le même
 * état, par construction.
 */

import {
  COULEUR_PAR_CRITICITE,
  COULEUR_PAR_ETAT_APPAREIL,
  Couleur,
  CriticiteAlerte,
  EtatAlimentation,
  EtatAppareil,
  EtatConnectivite,
  StatutAlerte,
  StatutCommande,
  StatutExploitation,
  StatutMembre,
  StatutStockAppareil,
  StatutSynchronisation,
} from '@genviz/shared/enums';

import {
  AIDE_STATUT_STOCK,
  LIBELLE_ALIMENTATION,
  LIBELLE_CONNECTIVITE,
  LIBELLE_CRITICITE,
  LIBELLE_ETAT_APPAREIL,
  LIBELLE_STATUT_ALERTE,
  LIBELLE_STATUT_COMMANDE,
  LIBELLE_STATUT_EXPLOITATION,
  LIBELLE_STATUT_MEMBRE,
  LIBELLE_STATUT_STOCK,
  LIBELLE_STATUT_SYNC,
} from '../i18n/fr';
import { classes } from '../lib/divers';

type TonBadge = 'neutre' | 'vert' | 'orange' | 'rouge' | 'noir' | 'bleu';

// BLEU et GRIS ajoutés au contrat le 23/09 (FR_00053). Sans eux, le back-office ne COMPILAIT
// plus : la version servie datait d'avant la charte, d'où « Éteint » encore affiché en noir.
const TON_PAR_COULEUR: Record<Couleur, TonBadge> = {
  [Couleur.VERT]: 'vert',
  [Couleur.ORANGE]: 'orange',
  [Couleur.ROUGE]: 'rouge',
  [Couleur.BLEU]: 'bleu',
  [Couleur.GRIS]: 'neutre',
  [Couleur.NOIR]: 'noir',
};

/** Valeur hexadécimale d'une couleur d'état, pour les marqueurs SVG de la carte. */
/** Charte des marqueurs, retour client FR_00053 : le NOIR ne sert plus qu'aux évènements. */
export const HEX_PAR_COULEUR: Record<Couleur, string> = {
  [Couleur.VERT]: '#16733A',
  [Couleur.ORANGE]: '#ED7D0E',
  [Couleur.ROUGE]: '#D0342C',
  /** Éteint volontairement par l'utilisateur. */
  [Couleur.BLEU]: '#1F6FB2',
  /** Plus de connectivité : on ne sait rien, on n'affirme rien. */
  [Couleur.GRIS]: '#8A97A6',
  /** Évènements du flux d'alertes seulement. */
  [Couleur.NOIR]: '#14181F',
};

export function Badge({
  ton = 'neutre',
  libelle,
  titre,
  avecPoint = true,
}: {
  ton?: TonBadge;
  libelle: string;
  titre?: string;
  avecPoint?: boolean;
}): JSX.Element {
  return (
    <span
      className={classes('gv-badge', ton !== 'neutre' && `gv-badge--${ton}`)}
      {...(titre ? { title: titre } : {})}
    >
      {avecPoint ? <span className="gv-badge__point" aria-hidden="true" /> : null}
      {libelle}
    </span>
  );
}

export function BadgeEtatAppareil({ etat }: { etat: EtatAppareil }): JSX.Element {
  return (
    <Badge
      ton={TON_PAR_COULEUR[COULEUR_PAR_ETAT_APPAREIL[etat]]}
      libelle={LIBELLE_ETAT_APPAREIL[etat]}
    />
  );
}

export function BadgeCriticite({ criticite }: { criticite: CriticiteAlerte }): JSX.Element {
  return (
    <Badge
      ton={TON_PAR_COULEUR[COULEUR_PAR_CRITICITE[criticite]]}
      libelle={LIBELLE_CRITICITE[criticite]}
    />
  );
}

export function BadgeStatutAlerte({ statut }: { statut: StatutAlerte }): JSX.Element {
  return (
    <Badge
      ton={statut === StatutAlerte.ACTIVE ? 'rouge' : 'neutre'}
      libelle={LIBELLE_STATUT_ALERTE[statut]}
    />
  );
}

const TON_STATUT_STOCK: Record<StatutStockAppareil, TonBadge> = {
  [StatutStockAppareil.EN_STOCK]: 'bleu',
  [StatutStockAppareil.ENREGISTRE]: 'vert',
  [StatutStockAppareil.RETIRE]: 'neutre',
  [StatutStockAppareil.SAV]: 'orange',
  [StatutStockAppareil.HORS_SERVICE]: 'noir',
};

export function BadgeStatutStock({ statut }: { statut: StatutStockAppareil }): JSX.Element {
  return (
    <Badge
      ton={TON_STATUT_STOCK[statut]}
      libelle={LIBELLE_STATUT_STOCK[statut]}
      titre={AIDE_STATUT_STOCK[statut]}
    />
  );
}

export function BadgeConnectivite({ etat }: { etat: EtatConnectivite }): JSX.Element {
  return (
    <Badge
      ton={etat === EtatConnectivite.EN_LIGNE ? 'vert' : 'rouge'}
      libelle={LIBELLE_CONNECTIVITE[etat]}
    />
  );
}

export function BadgeAlimentation({ etat }: { etat: EtatAlimentation }): JSX.Element {
  const ton: TonBadge =
    etat === EtatAlimentation.ON ? 'vert' : etat === EtatAlimentation.OFF ? 'noir' : 'neutre';
  return <Badge ton={ton} libelle={LIBELLE_ALIMENTATION[etat]} />;
}

const TON_STATUT_COMMANDE: Record<StatutCommande, TonBadge> = {
  [StatutCommande.EN_ATTENTE]: 'neutre',
  [StatutCommande.ENVOYEE]: 'bleu',
  [StatutCommande.ACQUITTEE]: 'bleu',
  [StatutCommande.APPLIQUEE]: 'vert',
  [StatutCommande.ECHOUEE]: 'rouge',
  [StatutCommande.EXPIREE]: 'orange',
  [StatutCommande.ANNULEE]: 'neutre',
};

export function BadgeStatutCommande({ statut }: { statut: StatutCommande }): JSX.Element {
  return <Badge ton={TON_STATUT_COMMANDE[statut]} libelle={LIBELLE_STATUT_COMMANDE[statut]} />;
}

const TON_SYNC: Record<StatutSynchronisation, TonBadge> = {
  [StatutSynchronisation.SYNCHRONISE]: 'vert',
  [StatutSynchronisation.EN_ATTENTE]: 'orange',
  [StatutSynchronisation.DIVERGENT]: 'rouge',
};

export function BadgeSynchronisation({
  statut,
}: {
  statut: StatutSynchronisation;
}): JSX.Element {
  return <Badge ton={TON_SYNC[statut]} libelle={LIBELLE_STATUT_SYNC[statut]} />;
}

const TON_STATUT_EXPLOITATION: Record<StatutExploitation, TonBadge> = {
  [StatutExploitation.ACTIVE]: 'vert',
  [StatutExploitation.SUSPENDUE]: 'orange',
  [StatutExploitation.SUPPRIMEE]: 'noir',
};

export function BadgeStatutExploitation({
  statut,
}: {
  statut: StatutExploitation;
}): JSX.Element {
  return (
    <Badge ton={TON_STATUT_EXPLOITATION[statut]} libelle={LIBELLE_STATUT_EXPLOITATION[statut]} />
  );
}

const TON_STATUT_MEMBRE: Record<StatutMembre, TonBadge> = {
  [StatutMembre.ACTIF]: 'vert',
  [StatutMembre.INVITATION_EN_ATTENTE]: 'bleu',
  [StatutMembre.DESACTIVE]: 'orange',
  [StatutMembre.RETIRE]: 'neutre',
};

export function BadgeStatutMembre({ statut }: { statut: StatutMembre }): JSX.Element {
  return <Badge ton={TON_STATUT_MEMBRE[statut]} libelle={LIBELLE_STATUT_MEMBRE[statut]} />;
}

/** Compteur d'alertes : neutre à zéro, rouge dès qu'il y a quelque chose à traiter. */
export function PastilleNombre({ valeur }: { valeur: number }): JSX.Element {
  return (
    <span className={classes('gv-pastille-nb', valeur > 0 && 'gv-pastille-nb--rouge')}>
      {valeur}
    </span>
  );
}
